import { ApproveAccrualReviewBody } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { ScheduleMatchResolution } from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleMatchResolutionKind } from "@common/types/domains/accounting/schedule";
import { MatchItemType, MatchableItem } from "../types";
import { parseMatchableItemSource } from "./matchItemSchedule";

function isUnmatch(resolution: ScheduleMatchResolution): boolean {
    return resolution.resolution === ScheduleMatchResolutionKind.unmatch;
}

function unmatchedInvoiceIds(
    resolutions: ScheduleMatchResolution[],
): Set<string> {
    return new Set(
        resolutions.filter(isUnmatch).map((resolution) => resolution.invoiceId),
    );
}

/** Drawer row ids to drop when the user picks Unmatch, matching the row action. */
export function unmatchedSelectionItemIds(
    resolutions: ScheduleMatchResolution[],
): string[] {
    return resolutions
        .filter(isUnmatch)
        .flatMap((resolution) => [
            `inv-${resolution.invoiceId}`,
            ...resolution.transactionIds.map(
                (transactionId) => `tx-${transactionId}`,
            ),
        ]);
}

export function hasSelectionLeftAfterUnmatch(options: {
    selectedItems: Array<Pick<MatchableItem, "id" | "type">>;
    resolutions: ScheduleMatchResolution[];
}): boolean {
    const unmatched = unmatchedInvoiceIds(options.resolutions);
    return options.selectedItems.some((item) => {
        if (item.type !== MatchItemType.INVOICE) {
            return true;
        }
        const source = parseMatchableItemSource(item);
        return !(
            source &&
            "invoiceId" in source &&
            unmatched.has(source.invoiceId)
        );
    });
}

export function hasTransactionsLeftAfterUnmatch(options: {
    transactionIds: number[];
    removeTransactionIds: number[];
    resolutions: ScheduleMatchResolution[];
}): boolean {
    if (options.removeTransactionIds.length > 0) {
        return true;
    }
    const unmatches = options.resolutions.filter(isUnmatch);
    if (
        unmatches.some((resolution) => resolution.transactionIds.length === 0)
    ) {
        return false;
    }
    const unmatchedTransactionIds = new Set(
        unmatches.flatMap((resolution) => resolution.transactionIds),
    );
    return options.transactionIds.some(
        (id) => !unmatchedTransactionIds.has(id),
    );
}

export function hasDocumentsLeftAfterUnmatch(options: {
    body: Pick<
        ApproveAccrualReviewBody,
        "invoiceIds" | "billIds" | "matchedTransactionId"
    >;
    resolutions: ScheduleMatchResolution[];
}): boolean {
    const { body } = options;
    if ((body.billIds?.length ?? 0) > 0 || body.matchedTransactionId != null) {
        return true;
    }
    const unmatched = unmatchedInvoiceIds(options.resolutions);
    return (body.invoiceIds ?? []).some((id) => !unmatched.has(id));
}
